/**
 * SHEEP MADE SIMPLE
 * SMSSeasonalSummary
 *
 * Version: 1.1.0
 *
 * Responsibilities:
 * - Convert Seasonal Plan task records into display summaries
 * - Format saved machine values as member-facing copy
 * - Format New Zealand dates and timestamps
 * - Build complete plan summaries
 *
 * This module does NOT:
 * - Read from SMSFarmStorage
 * - Write to SMSFarmStorage
 * - Validate form submissions
 * - Make recommendations
 */

window.SMSSeasonalSummary = (() => {
  "use strict";

  const SUMMARY_VERSION = 1;

  const COMPLETED_STATUSES = Object.freeze([
    "completed",
    "not_applicable",
    "review_due",
    "stale"
  ]);

  const VALID_SUMMARY_STATUSES = Object.freeze([
    "not_started",
    "completed",
    "not_applicable",
    "review_due",
    "stale",
    "invalid"
  ]);

  const seasonDefinitions = new Map();

  const MONTHS_LONG = Object.freeze([
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]);

  const MONTHS_SHORT = Object.freeze([
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ]);

  function isPlainObject(value) {
    return (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    );
  }

  function cloneValue(value) {
    if (value === undefined) {
      return undefined;
    }

    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
  }

  function capitalise(value) {
    const text = String(value || "");

    if (!text) {
      return "";
    }

    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function isValidDate(value) {
    if (
      typeof value !== "string" ||
      value.trim() === ""
    ) {
      return false;
    }

    return !Number.isNaN(Date.parse(value));
  }

  /**
   * Handles a date-only value without allowing timezone conversion
   * to move the displayed date forwards or backwards.
   */
  function parseDateOnly(value) {
    const match = String(value || "").match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);

    if (
      !Number.isInteger(year) ||
      monthIndex < 0 ||
      monthIndex > 11 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return {
      year,
      monthIndex,
      day
    };
  }

  function formatDate(
    value,
    {
      includeYear = false,
      shortMonth = false
    } = {}
  ) {
    if (!value) {
      return "";
    }

    const dateOnly = parseDateOnly(value);

    if (dateOnly) {
      const months = shortMonth
        ? MONTHS_SHORT
        : MONTHS_LONG;

      const yearLabel = includeYear
        ? ` ${dateOnly.year}`
        : "";

      return (
        `${dateOnly.day} ` +
        `${months[dateOnly.monthIndex]}` +
        yearLabel
      );
    }

    if (!isValidDate(value)) {
      return "";
    }

    const options = {
      day: "numeric",
      month: shortMonth ? "short" : "long",
      timeZone: "Pacific/Auckland"
    };

    if (includeYear) {
      options.year = "numeric";
    }

    return new Intl.DateTimeFormat(
      "en-NZ",
      options
    ).format(new Date(value));
  }

  function formatTime(value) {
    if (!isValidDate(value)) {
      return "";
    }

    return new Intl.DateTimeFormat("en-NZ", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Pacific/Auckland"
    })
      .format(new Date(value))
      .replace(/\bAM\b/, "am")
      .replace(/\bPM\b/, "pm");
  }

  function formatUpdatedLabel(updatedAt) {
    if (!isValidDate(updatedAt)) {
      return "";
    }

    const date = formatDate(updatedAt, {
      shortMonth: true
    });

    const time = formatTime(updatedAt);

    if (!date) {
      return "";
    }

    return time
      ? `Updated ${date}, ${time}`
      : `Updated ${date}`;
  }

  function formatLastUpdatedLabel(updatedAt) {
    if (!isValidDate(updatedAt)) {
      return "Last updated —";
    }

    const timestamp = new Date(updatedAt).getTime();
    const difference = Date.now() - timestamp;
    const days = Math.max(
      0,
      Math.floor(difference / 86400000)
    );

    if (days === 0) {
      return "Last updated today";
    }

    if (days === 1) {
      return "Last updated yesterday";
    }

    if (days < 30) {
      return `Last updated ${days} days ago`;
    }

    return `Last updated ${formatDate(updatedAt, {
      includeYear: true,
      shortMonth: true
    })}`;
  }

  function formatList(items) {
    const cleanItems = Array.isArray(items)
      ? items.filter(Boolean)
      : [];

    if (cleanItems.length === 0) {
      return "";
    }

    if (cleanItems.length === 1) {
      return cleanItems[0];
    }

    if (cleanItems.length === 2) {
      return `${cleanItems[0]} and ${cleanItems[1]}`;
    }

    return (
      cleanItems.slice(0, -1).join(", ") +
      ` and ${cleanItems[cleanItems.length - 1]}`
    );
  }

  function normaliseSecondary(value) {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map(String);
    }

    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return [];
    }

    return [String(value)];
  }

  function safeLabel(mapping, value, fallback = "") {
    if (
      !mapping ||
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return fallback;
    }

    return mapping[value] || fallback;
  }

  function validateStoredTask(task) {
    if (
      window.SMSSeasonalTaskMetadata &&
      typeof window.SMSSeasonalTaskMetadata
        .validateStoredTask === "function"
    ) {
      return window.SMSSeasonalTaskMetadata
        .validateStoredTask(task);
    }

    const errors = [];

    if (!isPlainObject(task)) {
      return {
        valid: false,
        errors: ["Task record must be an object."]
      };
    }

    if (!isPlainObject(task.data)) {
      errors.push("Task data is missing.");
    }

    if (!isPlainObject(task.meta)) {
      errors.push("Task metadata is missing.");
    }

    if (isPlainObject(task.meta)) {
      if (
        typeof task.meta.taskId !== "string" ||
        !task.meta.taskId
      ) {
        errors.push("Task ID is missing.");
      }

      if (
        typeof task.meta.season !== "string" ||
        !task.meta.season
      ) {
        errors.push("Season is missing.");
      }

      if (!Number.isInteger(task.meta.year)) {
        errors.push("Plan year is invalid.");
      }

      if (
        typeof task.meta.completed !== "boolean"
      ) {
        errors.push(
          "Task completion state is invalid."
        );
      }

      if (!isValidDate(task.meta.updatedAt)) {
        errors.push(
          "Task updated timestamp is invalid."
        );
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  function getDefinition(season) {
    return seasonDefinitions.get(season) || null;
  }

  function registerSeason(definition) {
    if (!isPlainObject(definition)) {
      throw new TypeError(
        "A valid season definition is required."
      );
    }

    const {
      season,
      taskOrder,
      taskTitles,
      labels,
      formatters
    } = definition;

    if (
      typeof season !== "string" ||
      !season.trim()
    ) {
      throw new TypeError(
        "The season definition requires a season."
      );
    }

    if (
      !Array.isArray(taskOrder) ||
      taskOrder.length === 0
    ) {
      throw new TypeError(
        `${season} requires a taskOrder array.`
      );
    }

    if (!isPlainObject(taskTitles)) {
      throw new TypeError(
        `${season} requires taskTitles.`
      );
    }

    if (!isPlainObject(formatters)) {
      throw new TypeError(
        `${season} requires task formatters.`
      );
    }

    taskOrder.forEach(taskId => {
      if (
        typeof taskTitles[taskId] !== "string"
      ) {
        throw new TypeError(
          `No title is registered for ${season}.${taskId}.`
        );
      }

      if (
        typeof formatters[taskId] !== "function"
      ) {
        throw new TypeError(
          `No formatter is registered for ${season}.${taskId}.`
        );
      }
    });

    seasonDefinitions.set(
      season,
      Object.freeze({
        season,
        taskOrder: Object.freeze([...taskOrder]),
        taskTitles: Object.freeze({
          ...taskTitles
        }),
        labels: Object.freeze({
          ...(labels || {})
        }),
        formatters: Object.freeze({
          ...formatters
        })
      })
    );

    return getDefinition(season);
  }

  function createEmptySummary({
    season,
    year,
    taskId
  }) {
    const definition = getDefinition(season);

    return {
      taskId,
      season,
      year,
      title:
        definition?.taskTitles?.[taskId] ||
        capitalise(taskId),
      primary: "Not completed yet",
      secondary: [],
      status: "not_started",
      updatedAt: null,
      updatedLabel: "",
      version: SUMMARY_VERSION
    };
  }

  function createInvalidSummary({
    season,
    year,
    taskId,
    message
  }) {
    const definition = getDefinition(season);

    return {
      taskId,
      season,
      year,
      title:
        definition?.taskTitles?.[taskId] ||
        capitalise(taskId),
      primary:
        "Saved information could not be displayed",
      secondary: [
        message ||
          "Please review this decision in your Seasonal Plan"
      ],
      status: "invalid",
      updatedAt: null,
      updatedLabel: "",
      version: SUMMARY_VERSION
    };
  }

  function resolveSummaryStatus(
    formattedStatus,
    reviewStatus
  ) {
    if (
      reviewStatus &&
      ["review_due", "stale"].includes(
        reviewStatus.status
      )
    ) {
      return reviewStatus.status;
    }

    if (
      VALID_SUMMARY_STATUSES.includes(
        formattedStatus
      )
    ) {
      return formattedStatus;
    }

    return "completed";
  }

  function getTaskSummary({
    season,
    year,
    taskId,
    task,
    reviewStatus = null
  }) {
    const definition = getDefinition(season);

    if (!definition) {
      return createInvalidSummary({
        season,
        year,
        taskId,
        message:
          `No summary definition is registered for ${capitalise(
            season
          )}.`
      });
    }

    if (
      !definition.taskOrder.includes(taskId)
    ) {
      return createInvalidSummary({
        season,
        year,
        taskId,
        message:
          "This task is not registered for the selected season."
      });
    }

    if (!task) {
      return createEmptySummary({
        season,
        year,
        taskId
      });
    }

    const validation =
      validateStoredTask(task);

    if (!validation.valid) {
      console.warn(
        "[SMSSeasonalSummary] Invalid task:",
        {
          season,
          year,
          taskId,
          errors: validation.errors
        }
      );

      return createInvalidSummary({
        season,
        year,
        taskId
      });
    }

    const formatter =
      definition.formatters[taskId];

    let formatted;

    try {
      formatted = formatter({
        task: cloneValue(task),
        labels: definition.labels,
        helpers: {
          formatDate,
          formatList,
          safeLabel
        }
      });
    } catch (error) {
      console.error(
        "[SMSSeasonalSummary] Formatter failed:",
        {
          season,
          year,
          taskId,
          error
        }
      );

      return createInvalidSummary({
        season,
        year,
        taskId
      });
    }

    if (!isPlainObject(formatted)) {
      return createInvalidSummary({
        season,
        year,
        taskId,
        message:
          "The saved task summary is unavailable."
      });
    }

    const updatedAt =
      task.meta.updatedAt || null;

    return {
      taskId,
      season,
      year,
      title:
        formatted.title ||
        definition.taskTitles[taskId],
      primary:
        formatted.primary ||
        "Saved decision",
      secondary: normaliseSecondary(
        formatted.secondary
      ),
      status: resolveSummaryStatus(
        formatted.status,
        reviewStatus
      ),
      updatedAt,
      updatedLabel:
        formatUpdatedLabel(updatedAt),
      reviewStatus: reviewStatus
        ? cloneValue(reviewStatus)
        : null,
      version: SUMMARY_VERSION
    };
  }

  function getPlanSummary({
    season,
    year,
    plan = null,
    reviewStatuses = {}
  }) {
    const definition = getDefinition(season);

    if (!definition) {
      return {
        success: false,
        reason: "season_not_registered",
        season,
        year,
        title: `${capitalise(season)} ${year}`,
        tasks: [],
        completedTasks: 0,
        totalTasks: 0,
        remainingTasks: 0,
        percentage: 0,
        startedAt: null,
        updatedAt: null,
        updatedLabel: "Last updated —",
        version: SUMMARY_VERSION
      };
    }

    const planTasks =
      isPlainObject(plan?.tasks)
        ? plan.tasks
        : {};

    const tasks =
      definition.taskOrder.map(taskId => {
        return getTaskSummary({
          season,
          year,
          taskId,
          task: planTasks[taskId] || null,
          reviewStatus:
            reviewStatuses?.[taskId] || null
        });
      });

    const completedTasks = tasks.filter(task =>
      COMPLETED_STATUSES.includes(task.status)
    ).length;

    const totalTasks = tasks.length;
    const remainingTasks =
      totalTasks - completedTasks;

    const percentage = totalTasks
      ? Math.round(
          (completedTasks / totalTasks) * 100
        )
      : 0;

    const updatedAt =
      plan?.meta?.updatedAt ||
      tasks
        .map(task => task.updatedAt)
        .filter(Boolean)
        .sort()
        .reverse()[0] ||
      null;

    return {
      success: true,
      season,
      year,
      title: `${capitalise(season)} ${year}`,
      tasks,
      completedTasks,
      totalTasks,
      remainingTasks,
      percentage,
      startedAt:
        plan?.meta?.startedAt || null,
      updatedAt,
      updatedLabel:
        formatLastUpdatedLabel(updatedAt),
      version: SUMMARY_VERSION
    };
  }

  function getRegisteredSeasons() {
    return Array.from(
      seasonDefinitions.keys()
    );
  }

  /**
   * Spring 2026 definition
   */

  const SPRING_LABELS = Object.freeze({
    lambingStatus: Object.freeze({
      currently_lambing:
        "Currently lambing",
      finished_in_winter:
        "Finished lambing during winter",
      not_lambing:
        "Not lambing this season"
    }),

    dailyChecks: Object.freeze({
      once_daily:
        "Once daily checks",
      twice_daily:
        "Twice daily checks",
      three_times_daily:
        "Three times daily checks"
    }),

    eweCondition: Object.freeze({
      holding_well:
        "Ewes holding condition well",
      losing_some_condition:
        "Ewes losing some condition",
      needs_attention:
        "Ewe condition needs attention",
      not_applicable:
        "Not applicable this season"
    }),

    supplementation: Object.freeze({
      not_supplementing:
        "Not supplementing",
      supplementing:
        "Supplementing lactating ewes",
      considering_supplementation:
        "Considering supplementation",
      not_applicable:
        "Not applicable this season"
    }),

    currentRotation: Object.freeze({
      winter:
        "Winter grazing rotation",
      spring:
        "Spring grazing rotation"
    }),

    grassStatus: Object.freeze({
      sheep_keeping_up:
        "Sheep keeping up with grass",
      grass_getting_ahead:
        "Grass beginning to get ahead"
    }),

    growthStrategies: Object.freeze({
      continue_current_system:
        "Continue the current system",
      combine_grazing_shifts:
        "Combine grazing shifts",
      top_behind_sheep:
        "Top behind sheep",
      run_additional_grazing_stock:
        "Run additional grazing stock"
    }),

    wormRisk: Object.freeze({
      low:
        "Low worm risk",
      moderate:
        "Moderate worm risk",
      high:
        "High worm risk",
      unsure:
        "Unsure of current worm risk"
    }),

    feetStatus: Object.freeze({
      checked_no_trimming:
        "Feet checked — no trimming required",
      checked_trimmed:
        "Feet checked — trimming completed",
      not_checked:
        "Feet not checked"
    }),

    lambGrowth: Object.freeze({
      growing_well:
        "Lambs growing well",
      monitoring:
        "Monitoring lamb growth",
      some_falling_behind:
        "Some lambs falling behind",
      not_applicable:
        "Not applicable this season"
    }),

    procedures: Object.freeze({
      ear_tagging:
        "Ear tagging",
      tailing:
        "Tailing",
      castrating:
        "Castration"
    }),

    shearingStatus: Object.freeze({
      not_booked:
        "Shearing not booked",
      booked:
        "Shearing booked",
      completed:
        "Shearing completed",
      not_required:
        "Shearing not required this spring"
    }),

    flystrikeMonitoring: Object.freeze({
      not_started:
        "Flystrike monitoring not started",
      started:
        "Flystrike monitoring started"
    })
  });

  const SPRING_TASK_ORDER = Object.freeze([
    "lambingManagement",
    "eweAndLambSupport",
    "grazingRotation",
    "springHealth",
    "lambProcedures",
    "shearingAndFlystrike"
  ]);

  const SPRING_TASK_TITLES = Object.freeze({
    lambingManagement:
      "Lambing management",

    eweAndLambSupport:
      "Ewe support",

    grazingRotation:
      "Current grazing system",

    springHealth:
      "Spring health",

    lambProcedures:
      "Lamb procedures",

    shearingAndFlystrike:
      "Shearing and flystrike"
  });

  function formatLambingManagement({
    task,
    labels,
    helpers
  }) {
    const {
      lambingStatus,
      dailyChecks
    } = task.data;

    if (
      lambingStatus ===
      "currently_lambing"
    ) {
      return {
        title:
          SPRING_TASK_TITLES
            .lambingManagement,

        primary: helpers.safeLabel(
          labels.dailyChecks,
          dailyChecks,
          "Daily check frequency not selected"
        ),

        secondary: [
          labels.lambingStatus[
            lambingStatus
          ]
        ],

        status: "completed"
      };
    }

    if (
      lambingStatus ===
      "finished_in_winter"
    ) {
      return {
        title:
          SPRING_TASK_TITLES
            .lambingManagement,

        primary:
          labels.lambingStatus[
            lambingStatus
          ],

        secondary: [
          "No spring lambing checks required"
        ],

        status: "not_applicable"
      };
    }

    if (
      lambingStatus ===
      "not_lambing"
    ) {
      return {
        title:
          SPRING_TASK_TITLES
            .lambingManagement,

        primary:
          labels.lambingStatus[
            lambingStatus
          ],

        secondary: [],

        status: "not_applicable"
      };
    }

    return {
      title:
        SPRING_TASK_TITLES
          .lambingManagement,

      primary:
        "Lambing status not recorded",

      secondary: [],

      status: "completed"
    };
  }

  function formatEweAndLambSupport({
    task,
    labels,
    helpers
  }) {
    const {
      eweCondition,
      supplementation
    } = task.data;

    const notApplicable =
      eweCondition === "not_applicable" &&
      supplementation ===
        "not_applicable";

    if (notApplicable) {
      return {
        title:
          SPRING_TASK_TITLES
            .eweAndLambSupport,

        primary:
          "Not applicable this season",

        secondary: [],

        status: "not_applicable"
      };
    }

    const supplementationLabel =
      helpers.safeLabel(
        labels.supplementation,
        supplementation
      );

    return {
      title:
        SPRING_TASK_TITLES
          .eweAndLambSupport,

      primary: helpers.safeLabel(
        labels.eweCondition,
        eweCondition,
        "Ewe condition not recorded"
      ),

      secondary:
        supplementationLabel
          ? [supplementationLabel]
          : [],

      status: "completed"
    };
  }

  function formatGrazingRotation({
    task,
    labels,
    helpers
  }) {
    const {
      currentRotation,
      grassStatus,
      growthStrategies = []
    } = task.data;

    const strategyLabels =
      Array.isArray(growthStrategies)
        ? growthStrategies
            .map(value =>
              helpers.safeLabel(
                labels.growthStrategies,
                value
              )
            )
            .filter(Boolean)
        : [];

    const secondary = [];

    const grassLabel =
      helpers.safeLabel(
        labels.grassStatus,
        grassStatus
      );

    if (grassLabel) {
      secondary.push(grassLabel);
    }

    if (strategyLabels.length) {
      secondary.push(
        `Planned response: ${helpers.formatList(
          strategyLabels
        )}`
      );
    }

    return {
      title:
        SPRING_TASK_TITLES
          .grazingRotation,

      primary: helpers.safeLabel(
        labels.currentRotation,
        currentRotation,
        "Grazing rotation not recorded"
      ),

      secondary,

      status: "completed"
    };
  }

  function formatSpringHealth({
    task,
    labels,
    helpers
  }) {
    const {
      wormRisk,
      feetStatus,
      lambGrowth
    } = task.data;

    return {
      title:
        SPRING_TASK_TITLES
          .springHealth,

      primary: helpers.safeLabel(
        labels.wormRisk,
        wormRisk,
        "Worm risk not recorded"
      ),

      secondary: [
        helpers.safeLabel(
          labels.feetStatus,
          feetStatus
        ),

        helpers.safeLabel(
          labels.lambGrowth,
          lambGrowth
        )
      ].filter(Boolean),

      status: "completed"
    };
  }

  function formatLambProcedures({
    task,
    labels,
    helpers
  }) {
    const {
      status,
      plannedDate,
      plannedLambCount,
      completedDate,
      completedLambCount,
      procedures = []
    } = task.data;

    if (status === "not_required") {
      return {
        title:
          SPRING_TASK_TITLES
            .lambProcedures,

        primary:
          "Not required this season",

        secondary: [],

        status: "not_applicable"
      };
    }

    const procedureLabels =
      Array.isArray(procedures)
        ? procedures
            .map(value =>
              helpers.safeLabel(
                labels.procedures,
                value
              )
            )
            .filter(Boolean)
        : [];

    if (status === "completed") {
      return {
        title:
          SPRING_TASK_TITLES
            .lambProcedures,

        primary: completedDate
          ? `Completed ${helpers.formatDate(
              completedDate
            )}`
          : "Completed",

        secondary: [
          Number.isFinite(
            completedLambCount
          )
            ? `${completedLambCount} lambs completed`
            : null,

          procedureLabels.length
            ? helpers.formatList(
                procedureLabels
              )
            : null
        ].filter(Boolean),

        status: "completed"
      };
    }

    return {
      title:
        SPRING_TASK_TITLES
          .lambProcedures,

      primary: plannedDate
        ? `Planned for ${helpers.formatDate(
            plannedDate
          )}`
        : "Planned",

      secondary: [
        Number.isFinite(
          plannedLambCount
        )
          ? `${plannedLambCount} lambs`
          : null,

        procedureLabels.length
          ? helpers.formatList(
              procedureLabels
            )
          : null
      ].filter(Boolean),

      status: "completed"
    };
  }

  function formatShearingAndFlystrike({
    task,
    labels,
    helpers
  }) {
    const {
      shearingStatus,
      shearingDate,
      flystrikeMonitoring
    } = task.data;

    let primary = helpers.safeLabel(
      labels.shearingStatus,
      shearingStatus,
      "Shearing status not recorded"
    );

    if (
      shearingStatus === "booked" &&
      shearingDate
    ) {
      primary =
        `Shearing booked for ` +
        helpers.formatDate(shearingDate);
    }

    if (
      shearingStatus === "completed" &&
      shearingDate
    ) {
      primary =
        `Shearing completed ` +
        helpers.formatDate(shearingDate);
    }

    const flystrikeLabel =
      helpers.safeLabel(
        labels.flystrikeMonitoring,
        flystrikeMonitoring
      );

    return {
      title:
        SPRING_TASK_TITLES
          .shearingAndFlystrike,

      primary,

      secondary:
        flystrikeLabel
          ? [flystrikeLabel]
          : [],

      status:
        shearingStatus === "not_required"
          ? "not_applicable"
          : "completed"
    };
  }

  registerSeason({
    season: "spring",

    taskOrder: SPRING_TASK_ORDER,

    taskTitles: SPRING_TASK_TITLES,

    labels: SPRING_LABELS,

    formatters: {
      lambingManagement:
        formatLambingManagement,

      eweAndLambSupport:
        formatEweAndLambSupport,

      grazingRotation:
        formatGrazingRotation,

      springHealth:
        formatSpringHealth,

      lambProcedures:
        formatLambProcedures,

      shearingAndFlystrike:
        formatShearingAndFlystrike
    }
  });


  /**
   * Winter 2026 definition
   */

  const WINTER_LABELS = Object.freeze({
    dailyChecks: Object.freeze({
      once_daily: "Once daily checks",
      twice_daily: "Twice daily checks",
      three_times_daily: "Three times daily checks"
    }),
    newbornFeedingKnowledge: Object.freeze({
      reviewed: "Newborn feeding checks reviewed",
      not_reviewed: "Newborn feeding checks not yet reviewed",
      not_applicable: "Not applicable this season"
    }),
    severeWeatherPlan: Object.freeze({
      prepared: "Severe-weather plan prepared",
      not_prepared: "Severe-weather plan not yet prepared",
      not_applicable: "Not applicable this season"
    })
  });

  const WINTER_TASK_ORDER = Object.freeze([
    "winterPaddock",
    "lambingPaddock",
    "grazingRotation",
    "lambingPreparation",
    "earlyLambing"
  ]);

  const WINTER_TASK_TITLES = Object.freeze({
    winterPaddock: "Winter paddock",
    lambingPaddock: "Lambing paddock",
    grazingRotation: "Winter grazing rotation",
    lambingPreparation: "Lambing preparation",
    earlyLambing: "Early lambing plan"
  });

  function winterPaddockLabel(task) {
    const data = task.data || {};
    return {
      title: WINTER_TASK_TITLES.winterPaddock,
      primary: data.paddockName || data.paddockId || "Winter paddock selected",
      secondary: data.notes ? [data.notes] : [],
      status: "completed"
    };
  }

  function lambingPaddockLabel(task) {
    const data = task.data || {};
    if (data.applicability === "not_applicable") {
      return {
        title: WINTER_TASK_TITLES.lambingPaddock,
        primary: "Not applicable this season",
        secondary: [],
        status: "not_applicable"
      };
    }
    return {
      title: WINTER_TASK_TITLES.lambingPaddock,
      primary: data.paddockName || data.paddockId || "Lambing paddock selected",
      secondary: data.notes ? [data.notes] : [],
      status: "completed"
    };
  }

  function winterRotationLabel(task) {
    const data = task.data || {};
    const weeks = Number(data.plannedRotationWeeks);
    const shifts = Number(data.plannedGrazingShifts);
    return {
      title: WINTER_TASK_TITLES.grazingRotation,
      primary: Number.isFinite(weeks) ? `${weeks}-week rotation` : "Winter rotation planned",
      secondary: Number.isFinite(shifts) ? [`${shifts} grazing shifts`] : [],
      status: "completed"
    };
  }

  function lambingPreparationLabel(task) {
    const data = task.data || {};
    if (data.applicability === "not_applicable") {
      return {
        title: WINTER_TASK_TITLES.lambingPreparation,
        primary: "Not applicable this season",
        secondary: [],
        status: "not_applicable"
      };
    }
    const completed = [
      data.highRiskEwesReviewed,
      data.suppliesPrepared,
      data.helpPlanReady
    ].filter(Boolean).length;
    const secondary = [];
    if (data.highRiskEwesReviewed) secondary.push("Higher-risk ewes reviewed");
    if (data.suppliesPrepared) secondary.push("Basic lambing supplies prepared");
    if (data.helpPlanReady) secondary.push("Vet or experienced help identified");
    return {
      title: WINTER_TASK_TITLES.lambingPreparation,
      primary: `${completed} of 3 preparations ready`,
      secondary,
      status: "completed"
    };
  }

  function earlyLambingLabel({ task, labels, helpers }) {
    const data = task.data || {};
    if (data.applicability === "not_applicable") {
      return {
        title: WINTER_TASK_TITLES.earlyLambing,
        primary: "Not applicable this season",
        secondary: [],
        status: "not_applicable"
      };
    }
    return {
      title: WINTER_TASK_TITLES.earlyLambing,
      primary: helpers.safeLabel(labels.dailyChecks, data.dailyChecks, "Daily checks planned"),
      secondary: [
        helpers.safeLabel(labels.newbornFeedingKnowledge, data.newbornFeedingKnowledge),
        helpers.safeLabel(labels.severeWeatherPlan, data.severeWeatherPlan)
      ].filter(Boolean),
      status: "completed"
    };
  }

  registerSeason({
    season: "winter",
    taskOrder: WINTER_TASK_ORDER,
    taskTitles: WINTER_TASK_TITLES,
    labels: WINTER_LABELS,
    formatters: {
      winterPaddock: ({ task }) => winterPaddockLabel(task),
      lambingPaddock: ({ task }) => lambingPaddockLabel(task),
      grazingRotation: ({ task }) => winterRotationLabel(task),
      lambingPreparation: ({ task }) => lambingPreparationLabel(task),
      earlyLambing: earlyLambingLabel
    }
  });

  const api = {
    SUMMARY_VERSION,
    COMPLETED_STATUSES,
    VALID_SUMMARY_STATUSES,

    registerSeason,
    getDefinition,
    getRegisteredSeasons,

    getTaskSummary,
    getPlanSummary,

    createEmptySummary,
    createInvalidSummary,

    formatDate,
    formatTime,
    formatUpdatedLabel,
    formatLastUpdatedLabel,
    formatList,
    safeLabel
  };

  document.dispatchEvent(
    new CustomEvent(
      "sms:seasonal-summary-ready",
      {
        detail: {
          version: SUMMARY_VERSION,
          seasons:
            getRegisteredSeasons()
        }
      }
    )
  );

  return Object.freeze(api);
})();